import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { Badge } from '@flowwai-frontend/design-system'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { useCustomT } from '@flowwai-frontend/shared/common-components'

import type NavItemModel from '../../stores/models/NavItemModel/NavItemModel'
import useHover from '../../hooks/useHover'
import {
   containerClassName,
   iconContainerClassName,
   nameAndIconContainerClassName,
   nameClassName
} from './styles'

interface Props {
   item: NavItemModel
   renderCustomIcon?: () => React.ReactElement
   isExactMatch?: (id: string) => boolean
}

const DefaultNavItem = (props: Props): React.ReactElement => {
   const { item, isExactMatch = () => true, renderCustomIcon } = props
   const { name, slug, iconId } = item

   const location = useLocation()

   const t = useCustomT('navbar:common')
   const { theme } = useAppTheme()
   const { defaultNavItem: navItemTheme } = theme.navbar

   const hoverState = useHover()

   const [isActive, setIsActive] = useState<boolean>(false)

   const renderCount = (): React.ReactElement | null => {
      if (item.count === null) return null
      return <Badge variant={'GRAY'}>{item.count}</Badge>
   }

   const renderIcon = (slug: string): React.ReactElement | null => {
      if (!iconId) return null

      return (
         <div className={iconContainerClassName}>
            {isActive ? (
               <svg width={20} height={20} fill='none'>
                  <use
                     xlinkHref={`/assets/icons/iconsSprite.svg#selected-${iconId}`}
                  />
               </svg>
            ) : (
               <svg width={20} height={20} fill='none'>
                  <use xlinkHref={`/assets/icons/iconsSprite.svg#${iconId}`} />
               </svg>
            )}
         </div>
      )
   }

   if (!slug) {
      throw new Error('Item must have a slug')
   }

   return (
      <NavLink
         exact={isExactMatch(item.id)}
         to={slug}
         activeClassName={cn(
            navItemTheme.selectedBgColor,
            navItemTheme.selectedTextColor
         )}
         className={(isActive: boolean) => {
            setIsActive(isActive)

            return cn(containerClassName, {
               [navItemTheme.textColor]: !isActive,
               [navItemTheme.hoverBgColor]: hoverState.isHovered && !isActive
            })
         }}
         onMouseEnter={hoverState.handleMouseEnter}
         onMouseLeave={hoverState.handleMouseLeave}
      >
         <div className={nameAndIconContainerClassName}>
            {renderCustomIcon ? renderCustomIcon() : renderIcon(slug)}
            <p className={nameClassName} title={name}>
               {name}
            </p>
            {renderCount()}
         </div>
      </NavLink>
   )
}

export default observer(DefaultNavItem)
